import { ViewModel } from 'view_models/view_model'
import { Result } from 'entities/result'
import { Background } from 'entities/background'
import { Cancer } from 'entities/product_item'
import React, { ReactNode } from 'react'
import { Flex } from 'components/shared/flex'
import { Sample } from 'entities/sample'
import moment, { Moment } from 'moment'
import { Symptom } from 'components/send_history/result_v2'

export type ResultInfoV2 = {
    score: {
        miRNA_score: number
        personal_score: number
        level: RiskLevel
        probability: number
        ppv: number
        ML_score: number
        personal_risk_ratio: number
        max_personal_risk_ratio: number
        min_personal_risk_ratio: number
        middle_personal_risk_ratio: number
        high_personal_risk_ratio: number
    }
    plot: {
        x: number
        y: number
    }
}

export type RiskDataItem = {
    label: string
    rates: { [key: string]: number }
    text: {
        max: string
        mid: string | null
        min: string
    }
    comment: string
}
export type RiskData<T> = {
    [key in keyof T]: RiskDataItem
}
export type RiskLevel = '高' | '中' | '低'

export type RiskScore = {
    age: number
}
export class ResultV2ViewModel extends ViewModel<Result> {
    public background: Background
    public cancerJa: string
    private middlePPV: string
    private highPPV: string
    private sample: Sample

    constructor(result: Result, cancerJa: string, middlePPV: string, highPPV: string, sample: Sample) {
        super(result)
        this.background = sample.background!
        this.cancerJa = cancerJa
        this.middlePPV = middlePPV
        this.highPPV = highPPV
        this.sample = sample
    }
    public info(): ResultInfoV2 {
        if (!this.object.scoreJson) {
            throw new Error('scoreJson is blank.')
        }
        return JSON.parse(this.object.scoreJson) as ResultInfoV2
    }

    public cancerEn(): Cancer {
        return this.object.productItem!.nameEn!
    }

    public misignalRiskLevel = (): RiskLevel => {
        return this.info().score.level
    }

    // #########################################
    // ############# マイクロRNA Score ###############
    // #########################################
    public miRnaScore = (): number => {
        return Math.max(Math.min(Math.round(this.info().score.miRNA_score), 99), 1)
    }

    public miRnaRiskLevel = (): RiskLevel => {
        const miRnaRiskLevel = this.miRnaScore()
        if (miRnaRiskLevel < 50) {
            return '低'
        } else if (miRnaRiskLevel < 75) {
            return '中'
        } else {
            return '高'
        }
    }

    public isMiRnaRiskLevelUp = (prevRiskLevel: RiskLevel): boolean => {
        if (prevRiskLevel === '高') {
            return false
        }
        if (prevRiskLevel === '中' && this.miRnaRiskLevel() === '高') {
            return true
        }
        if (prevRiskLevel === '低' && this.miRnaRiskLevel() !== '低') {
            return true
        }
        return false
    }

    public isMiRnaRiskLevelDown = (prevRiskLevel: RiskLevel): boolean => {
        if (prevRiskLevel === '低') {
            return false
        }
        if (prevRiskLevel === '中' && this.miRnaRiskLevel() === '低') {
            return true
        }
        if (prevRiskLevel === '高' && this.miRnaRiskLevel() !== '高') {
            return true
        }
        return false
    }

    public personalScore = (): number => {
        return Math.max(Math.min(Math.round(this.info().score.personal_score), 99), 1)
    }

    public personalRiskLevel = (): string => {
        const score = this.personalScore()
        if (score < 50) {
            return '低'
        } else if (score < 75) {
            return '中'
        } else {
            return '高'
        }
    }

    public ngReasonView = (sample: Sample): ReactNode => {
        const isNg = sample.resultStatus === 'NG'
        if (!isNg) {
            return <></>
        }
        return (
            <div>
                <div>今回は、以下の理由で検査ができませんでした。</div>
                {sample.differentContainer && <div>・指定容器以外の容器でのご提出のため</div>}
                {sample.nonUrine && <div>・尿以外の検体をご提出のため</div>}
                {sample.quantityStatus === 'NG' && <div>・尿量が1ml以下であったため</div>}
            </div>
        )
    }

    private getBMI = (background: Background): number => {
        if (!background.weight || !background.height) {
            return 0
        }
        return Math.round((background.weight / (background.height / 100) ** 2) * 100) / 100
    }

    public age = () => {
        return `${this.background.age}歳`
    }

    public height = () => {
        return `${this.background.height}cm`
    }

    public BMI = () => {
        return `${this.getBMI(this.background)}`
    }

    public misignalScoreSummary(): string {
        const riskScore = this.misignalRiskLevel()
        if (riskScore === '低') {
            return '今回の検査で提出頂いた尿の分析結果と申込票を総合評価し、あなたが現時点でがんである可能性を判定しています。'
        } else if (riskScore === '中') {
            return `現時点でのあなたの${this.cancerJa}発症リスクは「中」です。これはあなたと同じ性別・年代における${this.cancerJa}へのかかりやすさよりも高いといえます。`
        } else {
            return `現時点でのあなたの${this.cancerJa}発症リスクは「高」です。これはあなたと同じ性別・年代における${this.cancerJa}へのかかりやすさよりも高いといえます。`
        }
    }

    public misignalScoreComment(): string {
        const riskScore = this.misignalRiskLevel()
        if (riskScore === '低') {
            return `今回の結果では、${this.cancerJa}発症リスクは低いと考えられます。${this.cancerJa}の初期症状に当てはまる症状がある方は、引き続き経過観察を行い、症状が強くなる場合は追加検査の受検をおすすめします。また、予防や健康状態を確認するためにも、miSignal®を定期的に受検下さい。`
        } else if (riskScore === '中') {
            return `今回の結果では、${this.cancerJa}の発症リスクが中と判定されました。${this.cancerJa}の初期症状に当てはまる症状がない方は今すぐ追加の検査を受けていただく必要はありませんが、当てはまる症状がある方は、追加検査の受検をおすすめします。また、予防や健康状態を確認するためにも、miSignal®を定期的に受検下さい。`
        } else {
            return `今回の結果では、${this.cancerJa}の発症リスクが高いと判定されました。検査結果として示されるがん疾患リスクが高い場合に、直ちにがんと診断されるものではありませんが、${this.cancerJa}の初期症状に当てはまる症状の有無に関わらず、医師と相談のうえ、必要と考えられる追加検査を受検して下さい。`
        }
    }

    public miRnaScoreSummary(prevResultViewModel?: ResultV2ViewModel): string {
        if (!prevResultViewModel) {
            if (this.miRnaRiskLevel() === '低') {
                return `${this.cancerJa}のマイクロRNAがんリスクが"低"と判定されました。引き続き、規則正しい生活習慣と定期的な検診の受検を心がけましょう。`
            } else if (this.miRnaRiskLevel() === '中') {
                return `${this.cancerJa}のマイクロRNAがんリスクが"中"と判定されました。規則正しい生活習慣と定期的な検診の受検を心がけましょう。`
            } else {
                return `${this.cancerJa}のマイクロRNAがんリスクが"高"と判定されました。より一層、規則正しい生活習慣と定期的な検診の受検を心がけましょう。`
            }
        } else {
            const prevRiskLevel = prevResultViewModel.miRnaRiskLevel()
            if (this.isMiRnaRiskLevelUp(prevRiskLevel)) {
                return 'マイクロRNAがんリスクが前回よりも上がっています。生活習慣の見直しや定期的な検診の受検を心がけましょう。'
            } else if (prevRiskLevel === this.miRnaRiskLevel()) {
                return 'マイクロRNAがんリスクの値が前回と同程度です。引き続き、生活習慣の見直しや定期的な検診の受検を心がけましょう。'
            } else if (this.isMiRnaRiskLevelDown(prevRiskLevel)) {
                return 'マイクロRNAがんリスクの値が前回と比べて改善しました。引き続き、生活習慣の見直しや定期的な検診の受検を心がけましょう。'
            } else {
                return ''
            }
        }
    }

    public miRnaScoreComment(): string {
        if (this.miRnaRiskLevel() === '低') {
            return `${this.cancerJa}のリスクが同世代・性別と同程度と言えます。しかしながら、安心は禁物です。${this.cancerJa}は自覚症状を伴わず発症、進行する可能性があります。健康的な生活習慣を心がけ、早期発見のために定期的な検査の受検を行いましょう。`
        } else if (this.miRnaRiskLevel() === '中') {
            return `同世代・性別と比べて${this.cancerJa}のリスクが高いと言えます。直ちにがんと診断されるものではありませんが、総合評価でもリスク"高"となっている場合は、医師と相談のうえ、必要と考えられる追加検査を受検して下さい。`
        } else {
            return `同世代・性別と比べて${this.cancerJa}のリスクが高いと言えます。直ちにがんと診断されるものではありませんが、総合評価でもリスク"高"となっている場合は、医師と相談のうえ、必要と考えられる追加検査を受検して下さい。`
        }
    }

    public personalScoreSummary(): string {
        if (this.personalRiskLevel() === '低') {
            return `${this.cancerJa}のパーソナルがんリスクが低く、理想的な状態です。引き続き、規則正しい生活習慣と定期的な検診の受検を心がけましょう。`
        } else if (this.personalRiskLevel() === '中') {
            return `${this.cancerJa}のパーソナルがんリスクが"中"と判定されました。年齢の上昇や生活習慣によってリスクが高まる可能性があります。定期的な検診の受検を心がけましょう。`
        } else {
            return `${this.cancerJa}のパーソナルがんリスクが"高"と判定されました。年齢の上昇や生活習慣によってリスクが高まる可能性があります。定期的な検診の受検を心がけましょう。`
        }
    }
    public personalScoreComment(): string {
        if (this.personalRiskLevel() === '低') {
            return `${this.cancerJa}発症リスクが低い体質、生活習慣であると考えられます。ただし、安心は禁物です。${this.cancerJa}リスクは年齢の影響を受けやすく、今後リスクが変動する可能性があります。今後も、健康的な生活習慣を心がけ、早期発見のために定期的な検査の受検を行いましょう。`
        } else if (this.personalRiskLevel() === '中') {
            return `${this.cancerJa}発症リスクがやや高い年齢、体質、生活習慣であると考えられます。直ちにがんと診断されるものではありませんが、総合評価でもリスク"高"となっている場合は、医師と相談のうえ、必要と考えられる追加検査を受検して下さい。総合評価が"低"や"中"の場合でも、今後も定期的な検査の受検を行いましょう。`
        } else {
            return `${this.cancerJa}発症リスクが高い年齢、体質、生活習慣であると考えられます。直ちにがんと診断されるものではありませんが、総合評価でもリスク"高"となっている場合は、医師と相談のうえ、必要と考えられる追加検査を受検して下さい。総合評価が"低"や"中"の場合でも、今後も定期的な検査の受検を行いましょう。`
        }
    }

    public symptomsOfCancer(symptoms: Symptom[]): string[] {
        return (this.background.symptoms
            ?.split(',')
            .filter((s) => symptoms.find((symptom) => symptom.value === Number(s))?.products?.includes(this.cancerEn())) ?? []) as string[]
    }

    public symptomsView(symptoms: Symptom[], fontSize?: number): ReactNode {
        const productSymptoms = this.symptomsOfCancer(symptoms)
        if (!productSymptoms || productSymptoms.length === 0) {
            return <>検査時点で、特に自覚される{this.cancerJa}の症状は認められていません</>
        }

        return (
            <Flex flexWrap="wrap" alignItems="center" style={{ fontSize: 15, width: '100%' }} className="">
                {productSymptoms?.map((symptom, index) => {
                    if (symptom.match('[0-9]+')) {
                        const s = symptoms.filter((s) => s.products.includes(this.cancerEn())).find((s) => s.value === Number(symptom))
                        return (
                            <Flex key={'symptom-' + index} alignItems="center" style={{ width: '33%', marginBottom: 5, minHeight: 22 }} className="">
                                <div style={{ height: 15, width: 15, borderRadius: 10, background: 'black', marginRight: 2 }}></div>
                                <div style={{ fontSize: fontSize }}>{s?.label}</div>
                            </Flex>
                        )
                    } else {
                        return (
                            <Flex key={'symptom-' + index} alignItems="center" style={{ width: '33%', marginBottom: 5, minHeight: 22 }} className="">
                                <div style={{ height: 15, width: 15, borderRadius: 10, background: 'black', marginRight: 2 }}></div>
                                <div style={{ fontSize: fontSize }}>{symptom}</div>
                            </Flex>
                        )
                    }
                })}
            </Flex>
        )
    }

    public getNextExamDate(symptoms: Symptom[]): Moment {
        let next = moment(this.object.createdAt).add('M', 6)
        if (this.misignalRiskLevel() === '低' || (this.misignalRiskLevel() === '中' && this.symptomsOfCancer(symptoms).length === 0)) {
            // 低リスクまたは中リスク症状なしの場合は1年に1回
            next = moment(this.object.createdAt).add('year', 1)
        }
        return next
    }

    public getNextExamMonth(symptoms: Symptom[]): number {
        const date = moment(this.object.createdAt)
        const next = this.getNextExamDate(symptoms)
        return next.diff(date, 'M')
    }

    public getNextExamDateText(symptoms: Symptom[]): string {
        return this.getNextExamDate(symptoms).format('YYYY年MM月')
    }

    public getHighTextPosition(): { top: number; right: number } {
        return { top: 2, right: 5 }
    }
    public getMiddleTextPosition(): { bottom: number; left: number } {
        return { bottom: 135, left: 120 }
    }
    public getLowTextPosition(): { bottom: number; left: number } {
        return { bottom: 50, left: 40 }
    }
}
